import { useCallback } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CheckboxCellProps {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export function CheckboxCell({
  value,
  onChange,
  disabled = false,
  className,
}: CheckboxCellProps) {
  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled) {
        onChange(!value);
      }
    },
    [value, onChange, disabled]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault();
        onChange(!value);
      }
    },
    [value, onChange, disabled]
  );

  return (
    <div
      className={cn(
        'w-full h-full flex items-center justify-center',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role="checkbox"
      aria-checked={value}
    >
      <div
        className={cn(
          'w-5 h-5 border-2 rounded transition-colors cursor-pointer',
          'flex items-center justify-center',
          value
            ? 'bg-blue-500 border-blue-500'
            : 'bg-white border-gray-300 hover:border-gray-400',
          disabled && 'cursor-not-allowed'
        )}
      >
        {value && <Check className="h-3.5 w-3.5 text-white stroke-[3]" />}
      </div>
    </div>
  );
}

export default CheckboxCell;
